import React, { useState, useEffect } from "react";
import { useStore } from "./store";
import { useAutoRefresh } from "./hooks/useAutoRefresh";
import api from "./api";
import {
  ArrowDownToLine, QrCode, ScanLine, Building, Bitcoin, Send, X,
  CreditCard, ShoppingBag, ArrowLeftRight, ArrowUpRight, ArrowDownRight,
  PiggyBank, Shield, Lock, Plus, Sparkles, AlertCircle
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

/* ═════════════════════════════════════════════════════════════════
   NEW WALLET SCREEN — per Founder spec
   - Cash: withdraw bank/crypto, scan/QR, transfer to others
   - Shopping: scan/QR/pay-in-shop only (no withdraw)
   - Pension/Insurance: locked 5 years, 8% monthly compound
═════════════════════════════════════════════════════════════════ */

export function WalletScreenV2({ initialTab }) {
  const { wallets, fetchWallets, fetchTransactions, transactions, setToast, fetchAll } = useStore();
  useAutoRefresh(() => { fetchAll(); fetchTransactions(); }, 15000);
  const [tab, setTab] = useState(initialTab || "cash");
  const [modal, setModal] = useState(null);

  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);
  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const tabs = [
    { id: "cash",      label: "Cash",      icon: "💰", color: "#0E6B5E" },
    { id: "shopping",  label: "Shopping",  icon: "🛒", color: "#E8A020" },
    { id: "pension",   label: "Pension",   icon: "🏛️", color: "#3B82F6" },
    { id: "insurance", label: "Insurance", icon: "🛡️", color: "#BC13FE" },
  ];

  const balance = wallets?.[`${tab}Tgc`] || 0;
  const eur = (balance * 0.10).toFixed(2);

  return (
    <div className="screen">
      <div className="screen-header">
        <h1 className="screen-title">💼 Кошельки</h1>
        <div className="text-sm" style={{ opacity: 0.8 }}>
          Всего: {wallets ? (wallets.cashTgc + wallets.shoppingTgc + wallets.pensionTgc + wallets.insuranceTgc).toFixed(2) : 0} TGC
        </div>
      </div>

      {/* Wallet selector */}
      <div style={{ display: "flex", gap: 6, padding: "12px 16px 4px", overflowX: "auto" }}>
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`wallet-tab-${t.id}`}
            style={{
              flex: "0 0 auto", padding: "10px 14px", borderRadius: 12,
              border: tab === t.id ? `2px solid ${t.color}` : "1px solid var(--border)",
              background: tab === t.id ? `${t.color}15` : "white",
              color: tab === t.id ? t.color : "var(--text)",
              fontSize: 13, fontWeight: 600, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
            }}>
            <span style={{ fontSize: 16 }}>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {/* Big balance card */}
      <div style={{ padding: 16 }}>
        <div className="card" style={{
          background: `linear-gradient(135deg, ${tabs.find(t => t.id === tab).color} 0%, #0D1B4B 100%)`,
          color: "white", textAlign: "center", padding: 24,
        }}>
          <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>
            {tab} Wallet
          </div>
          <div style={{ fontSize: 36, fontWeight: 900, letterSpacing: -1 }}>
            {balance.toFixed(2)}
          </div>
          <div style={{ fontSize: 13, opacity: 0.9 }}>TGC · ≈ €{eur}</div>
        </div>

        {tab === "cash" && <CashActions setModal={setModal} />}
        {tab === "shopping" && <ShoppingActions setModal={setModal} />}
        {tab === "pension" && <LockedFundActions type="pension" />}
        {tab === "insurance" && <LockedFundActions type="insurance" />}

        {/* Transactions for this wallet */}
        <div className="text-bold mt-md mb-sm">История операций</div>
        {transactions.filter((tx) => tx.wallet === tab || tx.fromWallet === tab || tx.toWallet === tab).slice(0, 20).map((tx) => (
          <div key={tx.txId} className="tx-row">
            <div className="tx-icon" style={{ background: tx.amount > 0 ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.1)" }}>
              {tx.amount > 0 ? <ArrowDownRight size={20} color="#10B981" /> : <ArrowUpRight size={20} color="#EF4444" />}
            </div>
            <div className="flex-1">
              <div className="text-bold text-sm">{tx.note || tx.type.replace(/_/g, " ")}</div>
              <div className="text-hint text-sm">
                {new Date(tx.createdAt).toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className={tx.amount > 0 ? "text-success text-bold" : "text-bold"}>
                {tx.amount > 0 ? "+" : ""}{(tx.amount || 0).toFixed(2)}
              </div>
              <div className="text-hint text-sm">TGC</div>
            </div>
          </div>
        ))}
        {transactions.filter((tx) => tx.wallet === tab || tx.fromWallet === tab || tx.toWallet === tab).length === 0 && (
          <p className="text-center text-hint text-sm" style={{ padding: 30 }}>
            Пока нет операций
          </p>
        )}
      </div>

      {modal === "withdraw" && <WithdrawModal onClose={() => setModal(null)} />}
      {modal === "transfer" && <TransferModalV2 onClose={() => setModal(null)} />}
      {modal === "scan" && <ScanModal onClose={() => setModal(null)} />}
      {modal === "qr" && <ReceiveQRModal onClose={() => setModal(null)} type={tab} />}
      {modal === "topup" && <TestTopupModal onClose={() => setModal(null)} />}
    </div>
  );
}

function CashActions({ setModal }) {
  const actions = [
    { icon: <ArrowDownToLine size={20} />, label: "Вывод",   testId: "cash-action-withdraw", onClick: () => setModal("withdraw") },
    { icon: <ArrowLeftRight size={20} />,  label: "Перевод", testId: "cash-action-transfer", onClick: () => setModal("transfer") },
    { icon: <ScanLine size={20} />,        label: "Сканер",  testId: "cash-action-scan",     onClick: () => setModal("scan") },
    { icon: <QrCode size={20} />,          label: "Получить QR", testId: "cash-action-qr",   onClick: () => setModal("qr") },
    { icon: <Plus size={20} />,            label: "Тест +TGC ≈ €100", testId: "cash-action-topup", onClick: () => setModal("topup") },
  ];
  return (
    <div className="grid-3 mt-md">
      {actions.map((a, i) => (
        <button key={i} onClick={a.onClick} data-testid={a.testId}
          style={{
            display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
            background: "white", border: "1px solid var(--border)", borderRadius: 14,
            padding: "14px 4px", cursor: "pointer", color: "var(--navy)",
            fontSize: 11, fontWeight: 600,
          }}>
          {a.icon}
          <span>{a.label}</span>
        </button>
      ))}
    </div>
  );
}

function ShoppingActions({ setModal }) {
  const actions = [
    { icon: <ScanLine size={20} />, label: "Сканер",          onClick: () => setModal("scan") },
    { icon: <QrCode size={20} />,   label: "Мой QR",          onClick: () => setModal("qr") },
    { icon: <ShoppingBag size={20} />, label: "В магазине",   onClick: () => useStore.getState().setToast("info", "Открой Marketplace из главного меню → выбери товар → оплата происходит из Shopping Wallet") },
  ];
  return (
    <>
      <div className="grid-3 mt-md">
        {actions.map((a, i) => (
          <button key={i} onClick={a.onClick}
            style={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
              background: "white", border: "1px solid var(--border)", borderRadius: 14,
              padding: "14px 4px", cursor: "pointer", color: "var(--navy)",
              fontSize: 11, fontWeight: 600,
            }}>
            {a.icon}
            <span>{a.label}</span>
          </button>
        ))}
      </div>
      <div className="card mt-md" style={{ background: "rgba(232,160,32,0.08)", borderColor: "rgba(232,160,32,0.3)" }}>
        <div className="text-bold text-sm">🛒 Только для покупок</div>
        <p className="text-sm text-muted" style={{ margin: "4px 0 0" }}>
          Shopping Wallet нельзя выводить на банк/крипту. Используется только для оплаты товаров и услуг через QR/сканер.
        </p>
      </div>
    </>
  );
}

function LockedFundActions({ type }) {
  const [info, setInfo] = useState(null);
  useEffect(() => { api.get("/pension/info").then((r) => setInfo(r.data)); }, []);
  if (!info) return <div className="center-col"><div className="spinner" /></div>;
  const isPension = type === "pension";
  const balance = isPension ? info.pensionTgc : info.insuranceTgc;
  const est1 = isPension ? info.estIn1Year : info.insuranceEstIn1Year;
  const est5 = isPension ? info.estIn5Years : info.insuranceEstIn5Years;

  return (
    <div className="mt-md">
      <div className="card mb-sm">
        <div className="row gap-sm mb-sm">
          <Lock size={16} color="#3B82F6" />
          <span className="text-bold">Заблокировано на 5 лет</span>
        </div>
        <div className="text-sm text-muted">
          До разблокировки: <b>{Math.floor(info.daysUntilUnlock / 365)} лет {info.daysUntilUnlock % 365} дней</b>
        </div>
      </div>

      <div className="card mb-sm" style={{ background: "rgba(59,130,246,0.08)" }}>
        <div className="row between mb-sm">
          <div className="text-bold">Стейкинг 8% / месяц</div>
          <span className="chip">Авто</span>
        </div>
        <p className="text-sm text-muted" style={{ margin: 0 }}>
          Вся сумма автоматически работает в стейкинге под 8% в месяц.
          Начисление 1 раз в месяц, проценты добавляются к телу (капитализация).
        </p>
      </div>

      <div className="grid-2 mb-sm">
        <div className="card">
          <div className="text-hint text-sm">Через 1 год</div>
          <div className="text-bold text-navy text-lg">{est1.toFixed(0)}</div>
          <div className="text-hint text-sm">TGC ≈ €{(est1 * 0.10).toFixed(0)}</div>
        </div>
        <div className="card">
          <div className="text-hint text-sm">Через 5 лет</div>
          <div className="text-bold text-success text-lg">{est5.toFixed(0)}</div>
          <div className="text-hint text-sm">TGC ≈ €{(est5 * 0.10).toFixed(0)}</div>
        </div>
      </div>

      <div className="card" style={{ background: "rgba(239,68,68,0.06)" }}>
        <div className="row gap-sm mb-sm"><AlertCircle size={16} color="#EF4444" /><span className="text-bold text-sm">Перевод/вывод</span></div>
        <p className="text-sm text-muted" style={{ margin: 0 }}>
          Только по запросу в компанию в экстренных случаях. Стандартная разблокировка — через 5 лет.
        </p>
      </div>

      <p className="text-center text-hint text-sm mt-md">
        💡 Для пополнения {isPension ? "Pension" : "Insurance"} переведи TGC из Cash Wallet
      </p>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   MODALS
═════════════════════════════════════════════════════════════════ */
function ModalShell({ title, onClose, children }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 500, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ background: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 430 }}>
        <div className="row between mb-md">
          <h3 style={{ margin: 0, color: "var(--navy)" }}>{title}</h3>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer" }}><X size={24} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function WithdrawModal({ onClose }) {
  const [method, setMethod] = useState("bank");
  const [amount, setAmount] = useState("");
  const [destination, setDestination] = useState("");
  const { wallets, fetchWallets, fetchTransactions, setToast } = useStore();
  const cash = wallets?.cashTgc || 0;

  const submit = async () => {
    try {
      await api.post("/wallets/withdraw", { method, amount: parseFloat(amount), destination });
      await fetchWallets();
      await fetchTransactions();
      setToast("success", `Вывод ${amount} TGC → ${method.toUpperCase()} (DEMO)`);
      onClose();
    } catch (e) {
      setToast("error", e.response?.data?.detail || "Ошибка");
    }
  };

  return (
    <ModalShell title="Вывод средств" onClose={onClose}>
      <div className="text-hint text-sm mb-sm">Доступно: {cash.toFixed(2)} TGC</div>
      <div className="grid-2 mb-md">
        <button className={`btn ${method === "bank" ? "btn-teal" : "btn-outline"}`} onClick={() => setMethod("bank")}>
          <Building size={16} /> Банк
        </button>
        <button className={`btn ${method === "crypto" ? "btn-teal" : "btn-outline"}`} onClick={() => setMethod("crypto")}>
          <Bitcoin size={16} /> Крипта
        </button>
      </div>
      <label className="input-label">Сумма TGC</label>
      <input type="number" className="input mb-md" value={amount} onChange={(e) => setAmount(e.target.value)} max={cash} />
      <label className="input-label">{method === "bank" ? "IBAN / Номер карты" : "Crypto address"}</label>
      <input className="input mb-md" value={destination} onChange={(e) => setDestination(e.target.value)}
        placeholder={method === "bank" ? "KZ12 3456 7890..." : "0xAbC..."} />
      <button className="btn btn-primary btn-full" disabled={!amount || !destination || parseFloat(amount) > cash} onClick={submit}>
        Вывести {amount || 0} TGC (DEMO)
      </button>
    </ModalShell>
  );
}

function TransferModalV2({ onClose }) {
  const [to, setTo] = useState("shopping");
  const [amount, setAmount] = useState("");
  const { wallets, fetchWallets, fetchTransactions, setToast } = useStore();
  const cash = wallets?.cashTgc || 0;

  const submit = async () => {
    try {
      await api.post("/wallets/transfer", { fromWallet: "cash", toWallet: to, amount: parseFloat(amount) });
      await fetchWallets();
      await fetchTransactions();
      setToast("success", `Cash → ${to}: ${amount} TGC`);
      onClose();
    } catch (e) {
      setToast("error", e.response?.data?.detail || "Ошибка");
    }
  };

  return (
    <ModalShell title="Перевод из Cash" onClose={onClose}>
      <div className="text-hint text-sm mb-sm">Доступно: {cash.toFixed(2)} TGC</div>
      <label className="input-label">Куда</label>
      <select className="input mb-md" value={to} onChange={(e) => setTo(e.target.value)}>
        <option value="shopping">🛒 Shopping</option>
        <option value="pension">🏛️ Pension (стейкинг 8%/мес)</option>
        <option value="insurance">🛡️ Insurance (стейкинг 8%/мес)</option>
      </select>
      <label className="input-label">Сумма TGC</label>
      <input type="number" className="input mb-md" value={amount} onChange={(e) => setAmount(e.target.value)} max={cash} />
      <button className="btn btn-primary btn-full" disabled={!amount || parseFloat(amount) > cash} onClick={submit}>
        Перевести {amount || 0} TGC
      </button>
    </ModalShell>
  );
}

function ScanModal({ onClose }) {
  return (
    <ModalShell title="Сканировать QR" onClose={onClose}>
      <div style={{
        position: "relative", height: 240, background: "#000", borderRadius: 12,
        display: "flex", alignItems: "center", justifyContent: "center", color: "#5EEAD4",
        marginBottom: 16, overflow: "hidden",
      }}>
        <ScanLine size={64} opacity={0.5} />
        <div className="scan-line" style={{ left: 0, right: 0, top: 0, bottom: 0 }} />
        <div style={{ position: "absolute", bottom: 12, fontSize: 12, opacity: 0.7 }}>
          Наведи камеру на QR (DEMO)
        </div>
      </div>
      <p className="text-sm text-muted text-center">
        Чтобы тестировать оплаты — открой <b>Карта мерчантов</b> и нажми на любого мерчанта.
      </p>
    </ModalShell>
  );
}

function ReceiveQRModal({ onClose, type }) {
  const { user } = useStore();
  const value = `tgc://${user?.userId}/${type}`;
  return (
    <ModalShell title="Получить TGC" onClose={onClose}>
      <div style={{ textAlign: "center" }}>
        <div style={{ display: "inline-block", padding: 16, background: "white", borderRadius: 12, border: "2px solid var(--border)" }}>
          <QRCodeSVG value={value} size={200} />
        </div>
        <p className="text-sm text-muted mt-md">
          Покажи QR отправителю. <br/>Поступления зачислятся на <b>{type}</b> кошелёк.
        </p>
      </div>
    </ModalShell>
  );
}

function TestTopupModal({ onClose }) {
  const { fetchWallets, fetchTransactions, setToast } = useStore();
  const submit = async () => {
    try {
      await api.post("/wallets/test-topup");
      await fetchWallets();
      await fetchTransactions();
      setToast("success", "+TGC ≈ €100 зачислено в Cash");
      onClose();
    } catch (e) {
      setToast("error", "Ошибка");
    }
  };
  return (
    <ModalShell title="Тестовое пополнение" onClose={onClose}>
      <div className="card mb-md" style={{ background: "rgba(232,160,32,0.08)" }}>
        <div className="text-bold mb-sm">🎁 +TGC на €100</div>
        <p className="text-sm text-muted" style={{ margin: 0 }}>
          Только в DEMO. Для тестирования стейкингов, переводов, оплат и P2P. В продакшне эта кнопка будет недоступна.
        </p>
      </div>
      <button className="btn btn-gold btn-full" onClick={submit} data-testid="confirm-topup">
        Получить +TGC ≈ €100
      </button>
    </ModalShell>
  );
}

/* ═════════════════════════════════════════════════════════════════
   MAGIC 44 SCREEN — separate from Referrals
═════════════════════════════════════════════════════════════════ */
export function Magic44Screen() {
  const { referral, fetchReferral, user } = useStore();
  useEffect(() => { fetchReferral(); }, [fetchReferral]);
  useAutoRefresh(() => fetchReferral(), 15000);
  if (!referral) return <div className="center-col"><div className="spinner" /></div>;
  const progress = referral.magicProgress;
  const pct = (progress / 44) * 100;

  return (
    <div className="screen">
      <div className="screen-header">
        <h1 className="screen-title">✨ Magic 44</h1>
        <p className="screen-subtitle">AirDrop программа · 100€ на старте + бонусы</p>
      </div>
      <div style={{ padding: 16 }}>
        <div className="card mb-md" style={{
          background: "linear-gradient(135deg, #E8A020 0%, #BC13FE 100%)",
          color: "white", textAlign: "center", padding: 28,
        }}>
          <Sparkles size={48} style={{ margin: "0 auto 8px" }} />
          <div style={{ fontSize: 14, opacity: 0.9 }}>Твой прогресс</div>
          <div style={{ fontSize: 56, fontWeight: 900, letterSpacing: -2, margin: "4px 0" }}>
            {progress} / 44
          </div>
          <div style={{ background: "rgba(255,255,255,0.25)", borderRadius: 99, height: 12, overflow: "hidden", marginTop: 16 }}>
            <div style={{ width: `${pct}%`, height: "100%", background: "white", transition: "width 0.5s" }} />
          </div>
          <div style={{ fontSize: 12, opacity: 0.85, marginTop: 8 }}>
            {44 - progress} активных приглашений до разблокировки
          </div>
        </div>

        <div className="card mb-md">
          <h3 style={{ margin: "0 0 10px", color: "var(--navy)", fontSize: 16 }}>🎁 Что ты уже получил</h3>
          <div className="row gap-sm" style={{ alignItems: "center", padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
            <div style={{ width: 36, height: 36, borderRadius: 18, background: "rgba(16,185,129,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>✅</div>
            <div className="flex-1">
              <div className="text-bold text-sm">Стартовый AirDrop</div>
              <div className="text-hint text-sm">100€ = TGC на €100 при регистрации</div>
            </div>
            <div className="text-bold text-success">+1000</div>
          </div>
        </div>

        <div className="card mb-md">
          <h3 style={{ margin: "0 0 10px", color: "var(--navy)", fontSize: 16 }}>🚀 Что будет при 44</h3>
          {[
            ["💎 Diamond Stake разблокирован", "Стейкинг до 20% в месяц"],
            ["🎁 +5,000 TGC бонус", "Единоразовый AirDrop"],
            ["⚡ Energy Battery x3.0", "Множитель наград навсегда"],
            ["🏛️ DAO голос x2", "Двойной вес в голосованиях"],
            ["💳 Premium Visa", "Carbon-card с особыми привилегиями"],
          ].map(([title, desc], i) => (
            <div key={i} className="row gap-sm" style={{ padding: "10px 0", borderBottom: i < 4 ? "1px solid var(--border)" : "none" }}>
              <div style={{ minWidth: 24, fontSize: 18 }}>{title.split(" ")[0]}</div>
              <div className="flex-1">
                <div className="text-bold text-sm">{title.substring(title.indexOf(" ") + 1)}</div>
                <div className="text-hint text-sm">{desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="card" style={{ background: "rgba(232,160,32,0.08)" }}>
          <h3 style={{ margin: "0 0 10px", color: "var(--navy)", fontSize: 16 }}>📋 Условия</h3>
          <p className="text-sm text-muted" style={{ margin: "0 0 8px" }}>
            • Засчитываются только <b>активные</b> приглашённые (прошли KYC + сделали хотя бы 1 транзакцию)
          </p>
          <p className="text-sm text-muted" style={{ margin: "0 0 8px" }}>
            • Уровни L1-L8 все идут в зачёт (но один человек = +1)
          </p>
          <p className="text-sm text-muted" style={{ margin: 0 }}>
            • После 44-х продолжаешь получать L1-L8 % с каждой транзакции (до 44% от их активности)
          </p>
        </div>

        <p className="text-center text-hint text-sm mt-md">
          💡 Открой раздел <b>«Рефералы»</b> чтобы получить свою ссылку и QR-код
        </p>
      </div>
    </div>
  );
}
